import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { AddressDetailObject, AddressObject } from '@it2go/types';
import { PersonAddressEnum } from '../../../../../../../../types/src/services/enum/person-address.enum';
import { isInvalid } from '../../../../shared/helpers/validate';
import { UpsertAddressDialogStore } from './upsert-address-dialog.store';
import { mapAddress } from '../../../../shared/helpers/map-address.helper';
import { BaseDialogComponent } from '../../../../../components/kit/base-dialog/base-dialog.component';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import { AddressInputComponent } from '../../../../../components/kit/input/address-input/address-input.component';
import { TextAreaInputComponent } from '../../../../../components/kit/input/text-area-input/text-area-input.component';
import { AppFormGroup } from '../../../../../utils/form.utils';
import { fb } from '../../../../shared/shared.global-service';
import { contentGlobalSuccess } from '../../../content.error';

@Component({
    selector: 'app-upsert-address-dialog',
    templateUrl: './upsert-address-dialog.component.html',
    styleUrls: ['./upsert-address-dialog.component.scss'],
    providers: [UpsertAddressDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ReactiveFormsModule,
        DateInputComponent,
        AddressInputComponent,
        TextAreaInputComponent,
    ],
})
export class UpsertAddressDialogComponent extends BaseDialogComponent {
    public readonly store = inject(UpsertAddressDialogStore);

    protected form = new AppFormGroup({
        internNote: fb.control(''),
        validFrom: fb.control<Date | null>(null, Validators.required),
        address: fb.control<AddressDetailObject | null>(null, Validators.required),
    });

    private centerId: number = 0;
    private address: AddressObject | null = null;

    constructor() {
        super();

        this.centerId = this.dialogConfig.data.centerId;
        this.address = this.dialogConfig.data.address;

        this.form.patchValue({
            address: this.address?.address,
            validFrom: this.address ? new Date(this.address.validFrom) : undefined,
            internNote: this.address?.note,
        });
    }

    public override async save() {
        if (isInvalid(this.form)) return;

        if (!this.address) {
            await this.createPerson();
        } else {
            await this.updatePerson();
        }
    }

    private async updatePerson(): Promise<void> {
        const { address, validFrom, internNote } = this.form.value;

        this.store.api.updateAddress({
            id: this.address?.id ?? 0,
            address: mapAddress(address as AddressDetailObject),
            type: PersonAddressEnum.PERMANENT_RESIDENCE,
            validFrom: validFrom?.toISOString() ?? '',
            note: internNote ?? '',
            centerId: this.centerId,
        }).then(() => {
            contentGlobalSuccess.next('common.updated');
            this.dialogRef.close(true);
        });
    }

    private async createPerson(): Promise<void> {
        const { address, validFrom, internNote } = this.form.value;

        this.store.api.createAddress({
            address: mapAddress(address as AddressDetailObject),
            type: PersonAddressEnum.PERMANENT_RESIDENCE,
            validFrom: validFrom?.toISOString() ?? '',
            note: internNote ?? '',
            centerId: this.centerId,
        }).then(() => {
            contentGlobalSuccess.next('common.created');
            this.dialogRef.close(true);
        });
    }
}
